import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './cwv-analyser.module.css';
import { MIN_PANEL_WIDTH, MAX_PANEL_WIDTH, SEVERITY_LABEL, METRIC_COLOUR } from './constants';
import { CwvIssue, Status, Props } from './types';
import CopyIcon from './CopyIcon';

function errorCodeToStatus(code: string): Status {
  if (code === 'analyser_not_configured') return 'not_configured';
  if (code === 'rate_limit_exceeded') return 'rate_limited';
  if (code === 'api_key_expired') return 'api_key_expired';
  return 'error';
}

const CwvAnalyserPanel = ({
  code,
  context,
  pageTab,
  publisherId,
  runRef,
  onStatusChange,
  onWidthChange,
  onIssueCodeClick
}: Props) => {
  const [issues, setIssues] = useState<CwvIssue[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [analysedFor, setAnalysedFor] = useState<{ pageTab: string; context: 'head' | 'body' } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const isLoadingRef = useRef(false);
  const panelRef = useRef<HTMLElement | null>(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  const onResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      dragStartX.current = e.clientX;
      dragStartWidth.current = panelRef.current?.getBoundingClientRect().width ?? MIN_PANEL_WIDTH;

      const panelRightEdge = panelRef.current ? panelRef.current.getBoundingClientRect().right : window.innerWidth;

      const onMouseMove = (ev: MouseEvent) => {
        if (!isDragging.current) return;
        const delta = dragStartX.current - ev.clientX;
        const viewportMax = panelRightEdge - 8;
        const newWidth = Math.min(
          MAX_PANEL_WIDTH,
          viewportMax,
          Math.max(MIN_PANEL_WIDTH, dragStartWidth.current + delta)
        );
        onWidthChange?.(newWidth);
      };

      const onMouseUp = () => {
        isDragging.current = false;
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [onWidthChange]
  );

  const runAnalysis = useCallback(async () => {
    if (!code.trim() || isLoadingRef.current) return;

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    isLoadingRef.current = true;
    setAnalysedFor({ pageTab, context });
    setStatus('loading');
    setIssues([]);
    onStatusChange?.(true);

    try {
      const res = await fetch(`/api/v1/accounts/${publisherId}/cwv-analyse`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, context, pageTab }),
        signal: abortRef.current.signal
      });

      const data = await res.json();

      if (data.error) {
        isLoadingRef.current = false;
        const errStatus = errorCodeToStatus(data.error);
        setStatus(errStatus);
        const unblockSave = errStatus === 'not_configured' || errStatus === 'api_key_expired';
        onStatusChange?.(false, unblockSave);
        return;
      }

      isLoadingRef.current = false;
      setIssues(data.recommendations || []);
      setStatus('done');
      onStatusChange?.(false, true);
    } catch (err) {
      if (err && (err as any).name === 'AbortError') {
        isLoadingRef.current = false;
        return;
      }
      isLoadingRef.current = false;
      setStatus('error');
      onStatusChange?.(false, false);
    }
  }, [code, context, pageTab, publisherId, onStatusChange]);

  const stopAnalysis = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    isLoadingRef.current = false;
    setStatus('idle');
    setAnalysedFor(null);
  }, []);

  useEffect(() => {
    if (runRef) runRef.current = { run: runAnalysis, stop: stopAnalysis };
  }, [runAnalysis, stopAnalysis, runRef]);

  const errorCount = issues.filter(i => i.severity === 'error').length;
  const warnCount = issues.filter(i => i.severity === 'warning').length;
  const liveLabel =
    status === 'loading'
      ? 'Analysing your code, please wait'
      : status === 'done'
        ? issues.length > 0
          ? `${issues.length} CWV issue${issues.length > 1 ? 's' : ''} found`
          : 'No CWV issues detected'
        : status === 'error'
          ? 'Analysis unavailable'
          : 'CWV analyser ready';

  return (
    <section ref={panelRef} className={styles.panel} aria-label="CWV Impact Analysis" aria-busy={status === 'loading'}>
      <div
        className={styles.resizeHandle}
        onMouseDown={onResizeMouseDown}
        aria-hidden="true"
        title="Drag to resize panel"
      />
      <div className={styles.panelHeader}>
        <div className={styles.panelTitleRow}>
          <h2 className={styles.panelTitle}>CWV Impact Analysis</h2>
          {status === 'done' && (
            <span
              className={issues.length > 0 ? styles.statusBadgeError : styles.statusBadgeClean}
              aria-label={issues.length > 0 ? `${issues.length} issues found` : 'No issues detected'}
            >
              {issues.length > 0 ? `${issues.length} issue${issues.length > 1 ? 's' : ''} found` : 'No issues'}
            </span>
          )}
        </div>
        {analysedFor && (status === 'loading' || status === 'done') && (
          <>
            <p className={styles.panelSubtitle} aria-live="polite">
              {status === 'loading' ? 'Analysing' : 'Results for'} <strong>{analysedFor.pageTab}</strong>
              {' — '}
              <code className={styles.panelSubtitleCode}>&lt;{analysedFor.context}&gt;</code>
            </p>
            <p className={styles.panelSubtitle}>
              <span className={styles.summaryInfo} aria-label={` informational suggestions`}>
                ⓘ
              </span>
              AI Analyser can make mistakes. Please double-check responses.
            </p>
          </>
        )}
      </div>

      <div aria-live="polite" aria-atomic="true" aria-label={liveLabel} className={styles.liveRegion}>
        {status === 'idle' &&
          (!code.trim() ? (
            <p className={styles.idleHint} role="status">
              Add code to Analyse.
            </p>
          ) : (
            <p className={styles.idleHint}>
              Click <strong>Run Analyser</strong> to check this code for Core Web Vitals issues.
            </p>
          ))}

        {status === 'loading' && (
          <div className={styles.loadingWrapper} role="status">
            <span className={styles.spinner} aria-hidden="true" />
            <span className={styles.loadingText}>Analysing your code…</span>
          </div>
        )}

        {status === 'error' && (
          <p className={styles.errorHint} role="alert">
            ⚠ Analysis unavailable. Check your connection and try again.
          </p>
        )}

        {status === 'not_configured' && (
          <p className={styles.errorHint} role="alert">
            ⚠ Analyser not configured. Missing API Key credentials. Please contact support.
          </p>
        )}

        {status === 'api_key_expired' && (
          <p className={styles.errorHint} role="alert">
            ⚠ API key has expired or is invalid. Please contact support.
          </p>
        )}

        {status === 'rate_limited' && (
          <p className={styles.errorHint} role="alert">
            ⚠ Too many requests. Please wait a moment before running the analyser again.
          </p>
        )}

        {status === 'done' && issues.length === 0 && (
          <p className={styles.cleanHint} role="status">
            ✅ No CWV issues detected. Your code looks clean.
          </p>
        )}

        {status === 'done' && issues.length > 0 && (
          <>
            <div className={styles.summaryStrip} role="Issue summary" aria-label="Issue summary">
              <div className={styles.summaryRow}>
                {errorCount > 0 && (
                  <span className={styles.summaryError} aria-label={`${errorCount} critical issues`}>
                    ● {errorCount} critical
                  </span>
                )}
                {warnCount > 0 && (
                  <span className={styles.summaryWarn} aria-label={`${warnCount} warnings`}>
                    ⚠ {warnCount} warning{warnCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className={styles.summaryImpact} aria-hidden="true">
                ⚠ Your CWV will be affected
              </div>
            </div>

            <ul
              className={styles.issueList}
              aria-label={`${issues.length} Core Web Vitals issue${issues.length > 1 ? 's' : ''} detected`}
            >
              {issues.map(issue => (
                <li key={issue.id} className={styles.issueItem}>
                  <span className={`${styles.severityDot} ${styles[`dot_${issue.severity}`]}`} aria-hidden="true" />
                  <div className={styles.issueContent}>
                    <p className={styles.issueTitle}>
                      <b>Issue: </b>
                      <br />
                      {issue.message.text}
                    </p>
                    {issue.message.code && (
                      <div className={styles.issueCodeWrapper}>
                        <pre
                          className={`${styles.issueCode} ${onIssueCodeClick ? styles.issueCodeClickable : ''}`}
                          title={onIssueCodeClick ? 'Click to locate in editor' : undefined}
                          onClick={() => onIssueCodeClick?.(issue.message.code)}
                          role={onIssueCodeClick ? 'button' : undefined}
                          tabIndex={onIssueCodeClick ? 0 : undefined}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              if (onIssueCodeClick) onIssueCodeClick(issue.message.code);
                            }
                          }}
                          aria-label={onIssueCodeClick ? 'Click to locate this code in the editor' : undefined}
                        >
                          <code>{issue.message.code}</code>
                        </pre>
                        {onIssueCodeClick && (
                          <div className={styles.codeActions}>
                            <span className={styles.codeLocateHint} aria-hidden="true">
                              ↖ Double click to locate
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    <p className={styles.issueFix}>
                      🤖<b>AI Recommendation:</b> <br />
                      {issue.fix.text}
                    </p>
                    {issue.fix.code && (
                      <div className={styles.issueCodeWrapper}>
                        <pre className={styles.issueCode}>
                          <code>{issue.fix.code}</code>
                        </pre>
                        <div className={styles.codeActions}>
                          <button
                            className={styles.codeCopyBtn}
                            title="Copy to clipboard"
                            aria-label="Copy fix code"
                            onClick={() => navigator.clipboard.writeText(issue.fix.code)}
                          >
                            <CopyIcon /> Copy
                          </button>
                        </div>
                      </div>
                    )}
                    <div
                      className={styles.issueMeta}
                      aria-label={`Affects ${issue.metric}, severity: ${SEVERITY_LABEL[issue.severity]}`}
                    >
                      <span className={`${styles.metricPill} ${METRIC_COLOUR[issue.metric]}`} aria-hidden="true">
                        ↗ {issue.metric}
                      </span>
                      <span className={`${styles.categoryPill} ${styles[`sev_${issue.severity}`]}`} aria-hidden="true">
                        {SEVERITY_LABEL[issue.severity]}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
};

export default CwvAnalyserPanel;
